'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

/**
 * Thin progress bar at the top of the viewport that shows during page navigation.
 * - Starts when a same-origin Link is clicked (before middleware/data finish)
 * - Completes and fades out when the pathname changes (navigation done)
 */
export function NavigationProgress() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);
  const [width, setWidth] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completeRef = useRef(false);

  const clear = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const start = () => {
    completeRef.current = false;
    setVisible(true);
    setWidth(15);
    // Simulate incremental progress while waiting
    timerRef.current = setTimeout(() => setWidth(40), 200);
    timerRef.current = setTimeout(() => setWidth(65), 600);
    timerRef.current = setTimeout(() => setWidth(80), 1200);
  };

  const complete = () => {
    completeRef.current = true;
    setWidth(100);
    timerRef.current = setTimeout(() => {
      setVisible(false);
      setWidth(0);
    }, 350);
  };

  // When pathname changes, navigation is done
  useEffect(() => {
    if (completeRef.current === false && visible) {
      clear();
      complete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Listen for clicks on any <a> tag pointing to a same-origin path
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Skip external links, hash-only links, mailto, tel
      if (
        href.startsWith('http') ||
        href.startsWith('//') ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:')
      ) return;

      // Skip if it's the current page
      const dest = href.split('?')[0].split('#')[0];
      const current = pathname.split('?')[0];
      if (dest === current) return;

      clear();
      start();
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [pathname]);

  if (!visible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] h-[2px] pointer-events-none">
      <div
        className="h-full bg-primary transition-all ease-in-out"
        style={{
          width: `${width}%`,
          transitionDuration: width === 100 ? '200ms' : width <= 15 ? '50ms' : '400ms',
          boxShadow: '0 0 8px hsl(var(--primary) / 0.6)',
        }}
      />
    </div>
  );
}
